import React from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import { ORG_LEGAL_NAME, ORG_MAILING_ADDRESS } from '../config';

interface Props { navigate: (s: Screen) => void; user: User | null; }

const h2: React.CSSProperties = { fontSize: 18, marginTop: 28, marginBottom: 8 };
const p: React.CSSProperties = { fontSize: 15, lineHeight: 1.7, color: 'var(--charcoal)', marginBottom: 12 };
const li: React.CSSProperties = { fontSize: 15, lineHeight: 1.7, color: 'var(--charcoal)', marginBottom: 6 };

export default function Privacy({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';
  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Privacy Policy</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 28 }}>
        <h1 style={{ fontSize: 26, marginBottom: 6 }}>Privacy Policy</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>Effective date: August 4, 2026</p>

        <p style={p}>
          This Privacy Policy explains what Decode My Student, operated by the Brain
          Health and Wellness Coach Institute ("BHWCI," "we," "us"), collects, how we use
          it, and who we share it with. We have written it to describe what this
          application actually does — not generic boilerplate.
        </p>
        <p style={p}>
          Decode My Student is designed so that you never enter directly identifying
          information about a student. This is a design choice about how the app works;
          it is not a claim of compliance with any particular law. We do not claim HIPAA
          compliance — HIPAA does not apply to this service — and we do not make a formal
          FERPA-compliance certification. What we can do is describe our practices
          honestly, which is what follows.
        </p>

        <h2 style={h2}>1. Information you give us</h2>
        <p style={p}><strong>Account information.</strong> When you sign up we store your first and last name, email address, a securely hashed (bcrypt) version of your password, your school name if you provide one, and the date your trial started. If you join through a school license, we store the license you are attached to.</p>
        <p style={p}><strong>What you write about students.</strong> When you ask for help, we store:</p>
        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
          <li style={li}>The <strong>initials and grade level</strong> you enter to identify a student — and only those. The app does not ask for, and you agree not to enter, full names or other directly identifying details.</li>
          <li style={li}>The <strong>description you type or dictate</strong> about the situation, and the <strong>AI-generated analysis</strong> returned for it.</li>
          <li style={li}>Your notes and tracking: student "wins," strategies you are trying and their status, daily ratings of how those strategies went, and Friday debrief reflections.</li>
          <li style={li}>Your burnout / wellness check-ins (energy, connection, and feeling-supported scores).</li>
        </ul>
        <p style={p}><strong>Billing information.</strong> If you subscribe, payment is handled by Stripe. We do not see or store your full card number. We store the identifiers Stripe gives us (such as a customer and subscription id) and your plan and subscription status so we know whether your account is active.</p>

        <h2 style={h2}>2. Information collected automatically</h2>
        <p style={p}>
          We keep server logs needed to run and secure the Service, and we keep short-lived
          rate-limiting counters (stored in Netlify Blobs) tied to your login session so
          the app can't be abused. We do <strong>not</strong> use third-party advertising
          or analytics trackers, and we do not build advertising profiles about you.
        </p>

        <h2 style={h2}>3. Voice input</h2>
        <p style={p}>
          If you use the microphone to describe a situation, the speech-to-text conversion
          is performed by your own web browser's built-in speech recognition. Depending on
          which browser you use, the audio may be processed by the browser or operating
          system — and some browsers (for example, Chrome) send the audio to the browser
          vendor's own servers to produce the transcript. We receive only the resulting
          <strong> text</strong>, which is then handled exactly like text you typed. If you
          prefer not to use voice input, you can always type instead.
        </p>

        <h2 style={h2}>4. How your student descriptions are used by AI</h2>
        <p style={p}>
          To generate an analysis, the description you submit is sent to <strong>Anthropic's
          Claude API</strong>, which produces the response you see. Anthropic processes this
          text as our service provider to return the result.
        </p>
        <p style={p}>
          Anthropic states that it <strong>does not train its models on data submitted
          through its API by default</strong>. We rely on that default and do not opt in to
          any training use of your content. For the authoritative and current terms, please
          refer to Anthropic's own policies at{' '}
          <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontWeight: 600 }}>anthropic.com/legal</a>{' '}
          rather than relying on us to restate them.
        </p>

        <h2 style={h2}>5. Where your data is stored and who processes it</h2>
        <p style={p}>Your data is stored in a Postgres database and processed by a small set of service providers ("subprocessors"), each used only for its stated purpose:</p>
        <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
          <li style={li}><strong>Netlify</strong> — hosts the application, serverless functions, and the managed Postgres database (powered by Neon), and provides the rate-limiting storage.</li>
          <li style={li}><strong>Neon</strong> — the Postgres database engine where your account and student notes are stored.</li>
          <li style={li}><strong>Anthropic</strong> — the Claude AI that generates analyses from the descriptions you submit.</li>
          <li style={li}><strong>Stripe</strong> — payment processing for subscriptions.</li>
          <li style={li}><strong>Resend</strong> — sends transactional email (password resets, your welcome email, and messages you send us through the contact and feedback forms).</li>
        </ul>
        <p style={p}>We do not sell your personal information, and we do not share it with anyone for their own marketing.</p>

        <h2 style={h2}>6. How we protect your data</h2>
        <p style={p}>
          Passwords are stored only as bcrypt hashes — never in plain text. Your login
          session is kept in a secure, HttpOnly cookie, and the session token is stored on
          our side only as a hash. Traffic is served over HTTPS. Your Anthropic API key and
          other secrets stay on the server and are never sent to the browser. No system is
          perfectly secure, but these are the safeguards in place.
        </p>

        <h2 style={h2}>7. How long we keep it</h2>
        <p style={p}>
          We keep your account and the data you create for as long as your account exists,
          so your student profiles and history are there when you return. When you delete
          your account, the data is removed as described below. Expired login sessions are
          cleaned up automatically.
        </p>

        <h2 style={h2}>8. What you can delete</h2>
        <p style={p}>
          You are in control of your data. Within the app you can edit or remove individual
          student notes as you work. And from <strong>Account settings</strong> you can
          permanently <strong>delete your entire account</strong>. When you do, we delete
          your teacher record and everything linked to it — your students, the conversations
          and AI responses, wins, strategies, daily ratings, burnout check-ins, Friday
          debriefs, login sessions, and usage counters. Deletion is immediate and
          irreversible. If you have an active paid subscription, we cancel it as part of the
          deletion so you are not billed again.
        </p>
        <p style={p}>
          Note that a deletion request removes data from our live systems; routine encrypted
          backups held by our infrastructure providers age out on their normal cycles, and
          records we are legally required to keep (such as payment records held by Stripe)
          follow their own retention rules.
        </p>

        <h2 style={h2}>9. Children</h2>
        <p style={p}>
          The Service is for educators, who must be adults. It is not directed to children,
          and children do not have accounts. The app is intentionally built so that the
          information you enter about a student is limited to initials and grade level.
        </p>

        <h2 style={h2}>10. Changes to this policy</h2>
        <p style={p}>
          If we make a material change to this policy, we will update the effective date
          above and, where appropriate, notify you. Please review it periodically.
        </p>

        <h2 style={h2}>11. Contact</h2>
        <p style={p}>
          Questions or requests about your privacy? Use the{' '}
          <span style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('contact')}>Contact Us</span>{' '}
          form{ORG_MAILING_ADDRESS ? ', or write to us at:' : '.'}
        </p>
        {ORG_MAILING_ADDRESS && (
          <p style={p}>{ORG_LEGAL_NAME}<br />{ORG_MAILING_ADDRESS}</p>
        )}
      </div>
    </div>
  );
}
